import React, { useState, memo, useEffect, useMemo } from "react";
import { CountryID } from "@al/base-lib";
import JsCookie from "js-cookie";
import { withPage, WithI18nHocPropsType, WithReduxHocPropsType, WithPageHocPropsType } from "@al/next/al-ex";

import { COOKIE_KEYS_MAP, DEFAULT_ADDRESS } from "~/common/constants";
import HeadTitle from "~/components/ui/HeadTitle";
import withPerfCollect from "~/components/hoc/withPerfCollect";
import { tracker } from "~/common/tracker";

import { SensorConfig } from "../sensor-config";
import i18ns from "../i18n";

import prefetchHelper from "./redux/prefetch-helper";
import redux from "./redux";
import styles from "./styles.scss";
import { ProductDetailType, AddressItemType, IsDisable, SkuPriceType } from "./types";
import { baseErrorCode, pushNum, DKMap } from "./constants";
import Header from "./modules/Header";
import ProductImage from "./modules/ProductImage";
import Promotion from "./modules/Promotion";
import ProductInfo from "./modules/ProductInfo";
import Functions, { AddressProvinceItemType } from "./modules/Functions";
import Evaluate from "./modules/Evaluate";
// import SpacialForU from "./modules/SpacialForU";
import Description from "./modules/Description";
import BottomBar from "./modules/BottomBar";
import BulletinBoard from "./modules/BulletinBoard";
import FixedTab from "./components/FixedTab";
import NoProduct from "./components/NoProduct";
import NetworkError from "./components/NetworkError";
import HomeSkeleton from "./components/HomeSkeleton";
import ClientHomeSkeleton from "./components/HomeSkeleton/ClientHomeSkeleton";
import HomeLoading from "./components/HomeLoading";
import RedeemModal from "./components/RedeemModal";
import Specification from "./modules/Functions/Specification";
import ShopInfo, { ShopInfoType } from "./modules/ShopInfo";
import OpenAppBtn from "./modules/OpenAppBtn";

interface Props extends WithI18nHocPropsType, WithReduxHocPropsType, WithPageHocPropsType {
  asyncGetProductDetailBase: any;
  asyncGetUserAddressList: any;
  skuPrice: SkuPriceType;
}

function getReqCookieByName(name: string, cookie?: string): string | undefined {
  if (!cookie) return;
  const cookieArr = cookie.split("; ");
  for (let index = 0; index < cookieArr.length; index++) {
    const [key, val] = cookieArr[index].split("=");
    if (key === name) {
      return val;
    }
  }
}

const ProductDetail: React.FC<Props> = (props) => {
  const [isRedeemModal, setIsRedeemModal] = useState(false);
  const [shopInfo, setShopInfo] = useState<ShopInfoType>();
  // 一级类目
  const [categoryLevel1, setCategoryLevel1] = useState<string>();
  const { asyncGetProductDetailBase, asyncGetUserAddressList, ctx } = props;
  const dispatch = ctx.store.dispatch;
  const state = ctx.store.getState();
  const countryId = ctx.locale.countryId as CountryID;

  const { skuId, spuId } = useMemo(() => ctx.router.query, [ctx.router.query]) as { skuId: string; spuId: string };
  // 商品基础信息
  const detail: ProductDetailType = useMemo(() => asyncGetProductDetailBase?.value?.data, [
    asyncGetProductDetailBase?.isReady,
  ]);
  // 用户地址列表
  const addressList: AddressProvinceItemType[] = useMemo(() => asyncGetUserAddressList?.value?.data, [
    asyncGetUserAddressList?.isReady,
  ]);

  // 默认地址，从用户地址列表拿不到就用默认兜底地址
  const defaultAddress = useMemo(() => {
    // 这里优先选用户选过的地址, 包括用户选过的省份，切换国家仍要记住！！！。
    const addressCookieKey = `${COOKIE_KEYS_MAP.previousSelectAddress}_${countryId}`;
    const addressLabelCookieKey = `${COOKIE_KEYS_MAP.previousSelectAddressLabel}_${countryId}`;
    const [provinceId, cityId, districtId, villageId] = decodeURIComponent(
      (process.server
        ? getReqCookieByName(addressCookieKey, ctx.req?.headers.cookie)
        : JsCookie.get(addressCookieKey)) || ""
    ).split("_");
    const [provinceName, cityName, districtName, villageName] = decodeURIComponent(
      (process.server
        ? getReqCookieByName(addressLabelCookieKey, ctx.req?.headers.cookie)
        : JsCookie.get(addressLabelCookieKey)) || ""
    ).split("_");
    // 优先选择用户上次手动选择过的地址，先判断上次选中的地址是否可用，可用则选中，否则用用户默认最优地址，都没有则用兜底地址
    const lastSelectAddress = provinceId && {
      provinceId,
      cityId,
      districtId,
      villageId,
      provinceName,
      cityName,
      districtName,
      villageName,
    };
    return provinceId ? lastSelectAddress : DEFAULT_ADDRESS;
  }, [addressList, countryId]);

  if (!state.sku_detail.static.isInitDefaultAddress) {
    // 持久化默认地址
    dispatch(
      redux.actions.setDefaultAddress({ ...defaultAddress, countryId: ctx.locale.countryId } as AddressItemType)
    );
  }

  const handleGoBack = (): void => {
    setIsRedeemModal(true);
    setTimeout(() => {
      tracker.reCollectExposeItems();
    }, 100);
  };

  useEffect(() => {
    // 持久化商品基础信息
    if (detail) {
      dispatch(redux.actions.setBaseDetail(detail));
    }
    // 获取价格.
    if (defaultAddress && detail && spuId) {
      dispatch(redux.actions.updateSkuPrice({ skuId, spuId, isFirst: true }));
    }
  }, [detail, defaultAddress, skuId, spuId]);

  useEffect(() => {
    const promotion = props?.skuPrice?.promotion;
    const categoryInfos = detail?.categoryInfos;
    const channel = ctx.query?.channel;
    let saParams: any = {
      ...SensorConfig.homePage.pageInfo,
      commodityID: spuId,
    };
    if (promotion?.activityId) {
      saParams.advertisingID = promotion.activityId;
    }
    if (channel) {
      saParams.channel = channel;
    }
    categoryInfos?.forEach((item) => {
      saParams = {
        ...saParams,
        [`category_level${item.level}_name`]: item.name,
        [`category_level${item.level}_id`]: item.id,
      };
    });
    tracker.setPageConfig({
      sa: saParams,
    });
    tracker.enter({
      sa: {},
    });
    setCategoryLevel1(categoryInfos?.find((item) => item.level === 1)?.id);
    return (): void => {
      tracker.leave({
        sa: {},
      });
    };
  }, [detail, props?.skuPrice]);

  // 离开商详清除 state
  useEffect(() => {
    return (): void => {
      dispatch(redux.actions.resetState());
    };
  }, []);

  useEffect(() => {
    const handleJumpGoodsDetail = async (): Promise<void> => {
      const appTarget = "akulaku";
      const href = `ak://m.akulaku.com/1301?id=${spuId}&countryCode=ID&skuId=${skuId}`;
      const { success, data } = await ctx.store.dispatch(redux.actions.asyncMakeDeepLink({ content: href, appTarget }));
      if (success && data && data.actionKey) {
        const actionKey = data.actionKey.split("actionKey=");
        props.dispatch(redux.actions.setDeepLink(actionKey[1]?.split("&")?.[0]));
      }
    };
    handleJumpGoodsDetail();
  }, [skuId, spuId]);

  useEffect(() => {
    if (detail?.vendorId && detail?.shopId) {
      const getShopInfo = async (): Promise<void> => {
        const { success, data } = await ctx.store.dispatch(
          redux.actions.asyncGetShopInfo({ vendorId: detail.vendorId, shopId: detail.shopId })
        );
        if (success && data) {
          setShopInfo(data);
        }
      };
      getShopInfo();
    }
  }, [detail?.vendorId, detail?.shopId]);

  useEffect(() => {
    setTimeout(() => {
      window.history.pushState({ target: "current" }, "", location.href);
      // 如果用户在当前页刷新页面，就会一直push当前页进浏览器路由堆栈，需要记录push进去的数量，方便回退操作。
      const num = sessionStorage.getItem(pushNum) || "0";
      sessionStorage.setItem(pushNum, JSON.stringify(JSON.parse(num) + 1));
      window.addEventListener("popstate", handleGoBack);
    }, 500);

    return (): void => {
      window.removeEventListener("popstate", handleGoBack);
    };
  }, []);

  // 加载中显示骨架屏
  if (!asyncGetProductDetailBase.isReady || !asyncGetUserAddressList.isReady) {
    return <HomeSkeleton />;
  }

  // 请求基础信息接口没有内容，就说明商品不存在
  if (
    (asyncGetProductDetailBase.isReady && detail === null) ||
    // 特殊错误码也显示商品不存在
    (asyncGetProductDetailBase.isReady && !!baseErrorCode[asyncGetProductDetailBase?.value.errCode]) ||
    detail?.disabled === IsDisable.Disable
  ) {
    return <NoProduct />;
  }

  const handleCancelRedeemModal = (): void => {
    setIsRedeemModal(false);
    window.removeEventListener("popstate", handleGoBack);
    const num = sessionStorage.getItem(pushNum) || "1";
    sessionStorage.removeItem(pushNum);
    history.go(-JSON.parse(num));
  };

  // 请求基础信息接口失败，显示接口异常页面。
  if (asyncGetProductDetailBase.isReady && !asyncGetProductDetailBase?.value?.success) {
    const refresh = (): void => {
      // 刷新当前页
      window.location.replace(location.href);
    };
    return <NetworkError onRefresh={refresh} />;
  }

  return (
    <div className={styles.page}>
      {/* 页面标题 */}
      <HeadTitle title={detail.name} dkMap={categoryLevel1 ? DKMap[categoryLevel1] : ""} />
      {/* 通用悬浮头 */}
      <Header detail={detail} />
      {/* 打开app悬浮按钮 */}
      <OpenAppBtn />
      {/* 商品轮播图 */}
      <ProductImage images={detail.bannerImages} />
      {/* 商品促销 */}
      <Promotion />
      {/* 商品标题 */}
      <ProductInfo name={detail.name} />
      {/* 公告栏 */}
      <BulletinBoard />
      {/* 优惠券 */}
      <Functions detail={detail} addressList={addressList} />
      {/* 评价 */}
      <Evaluate spuId={spuId} />
      {/* 关联推荐 */}
      {/* <SpacialForU spuId={spuId} address={defaultAddress} /> */}
      {/* 商品规格 */}
      <ShopInfo shopInfo={shopInfo} />
      <Specification specificationInfos={detail?.specificationInfos} />
      {/* 商品描述 */}
      <Description spuId={spuId} skuId={skuId} />
      {/* 悬浮栏 */}
      <BottomBar detail={detail} />
      {/* 悬浮导航 */}
      <FixedTab />
      {/* 页面 Loading */}
      <HomeLoading />
      {/* 骨架屏 */}
      <ClientHomeSkeleton />

      {/* 返回拦截弹窗 */}
      <RedeemModal visible={isRedeemModal} onCancel={handleCancelRedeemModal} onDownLoadApp={handleCancelRedeemModal} />
    </div>
  );
};

export default withPerfCollect({
  needNetResource: true,
  criticalAPI: Object.values(prefetchHelper).map((item) => item.url),
})(
  withPage(({ withI18n, withRedux }) => [
    { prefetchApi: prefetchHelper },
    withI18n("rule", i18ns),
    withRedux("sku_detail", redux.reducer, (state: any) => ({
      skuPrice: state.static.skuPrice,
    })),
  ])(memo(ProductDetail))
);
